import { Head, router } from '@inertiajs/react'
import { Star, MessageSquare } from 'lucide-react'
import NavigationRail from '@/components/NavigationRail'
import { getInitials, getAvatarColor } from '@/lib/displayUtils'

interface Agent {
  id: number
  name: string
  email: string
  avatar_url: string | null
  role: string
}

interface Distribution {
  star: number
  count: number
  percentage: number
}

interface AgentStat {
  id: number
  name: string
  avatar_url: string | null
  email: string
  total: number
  average: number
  distribution: number[]
}

interface Comment {
  id: number
  rating: number
  comment: string
  agent_name: string
  agent_avatar_url: string | null
  created_at: string
}

interface RatingsIndexProps {
  currentAgent: Agent
  period: string
  total: number
  average: number
  distribution: Distribution[]
  agentStats: AgentStat[]
  recentComments: Comment[]
}

const PERIODS = [
  { value: 'all', label: 'Tudo' },
  { value: 'day', label: 'Último dia' },
  { value: 'week', label: 'Última semana' },
  { value: 'month', label: 'Último mês' },
]

function StarDisplay({ rating, size = 'md' }: { rating: number; size?: 'sm' | 'md' | 'lg' }) {
  const sizeClass = size === 'sm' ? 'text-sm' : size === 'lg' ? 'text-2xl' : 'text-base'
  return (
    <span className={`${sizeClass} text-amber-500`}>
      {[1, 2, 3, 4, 5].map(s => (
        <span key={s}>{s <= rating ? '★' : '☆'}</span>
      ))}
    </span>
  )
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function RatingsIndex({
  currentAgent,
  period,
  total,
  average,
  distribution,
  agentStats,
  recentComments,
}: RatingsIndexProps) {
  const handlePeriodChange = (newPeriod: string) => {
    router.get('/ratings', { period: newPeriod }, { preserveState: true })
  }

  return (
    <>
      <Head title="Avaliações" />
      <div className="h-screen flex bg-gray-50">
        <NavigationRail currentAgent={currentAgent} activePage="ratings" />

        <div className="flex-1 flex flex-col min-w-0 overflow-auto">
          <header className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-6 shrink-0">
            <h1 className="text-lg font-semibold text-gray-900">Avaliações</h1>
          </header>

          <div className="p-6 space-y-6">
            {/* Period filters */}
            <div className="flex gap-2">
              {PERIODS.map(p => (
                <button
                  key={p.value}
                  onClick={() => handlePeriodChange(p.value)}
                  className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors cursor-pointer ${
                    period === p.value
                      ? 'bg-gray-900 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Metric cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Average */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <p className="text-sm text-gray-500 mb-1">Média geral</p>
                <div className="flex items-center gap-3">
                  <span className="text-3xl font-bold text-gray-900">{average || '—'}</span>
                  {average > 0 && <StarDisplay rating={Math.round(average)} size="lg" />}
                </div>
              </div>

              {/* Total */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <p className="text-sm text-gray-500 mb-1">Total de avaliações</p>
                <span className="text-3xl font-bold text-gray-900">{total}</span>
              </div>

              {/* Distribution */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <p className="text-sm text-gray-500 mb-3">Distribuição</p>
                <div className="space-y-1.5">
                  {[...distribution].reverse().map(d => (
                    <div key={d.star} className="flex items-center gap-2 text-sm">
                      <span className="w-4 text-right text-gray-600">{d.star}</span>
                      <span className="text-amber-500">★</span>
                      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-amber-400 rounded-full transition-all"
                          style={{ width: `${d.percentage}%` }}
                        />
                      </div>
                      <span className="w-8 text-right text-gray-500 text-xs">{d.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Agent ranking table */}
            {agentStats.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-200">
                  <h2 className="text-sm font-semibold text-gray-900">Ranking por agente</h2>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                      <th className="text-left px-5 py-3 font-medium">Agente</th>
                      <th className="text-center px-3 py-3 font-medium">Média</th>
                      <th className="text-center px-3 py-3 font-medium">Total</th>
                      <th className="text-left px-3 py-3 font-medium">Distribuição</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {agentStats.map(agent => (
                      <tr key={agent.id} className="group">
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2.5">
                            {agent.avatar_url ? (
                              <img
                                src={agent.avatar_url}
                                alt={agent.name}
                                className="w-7 h-7 rounded-full object-cover"
                              />
                            ) : (
                              <div
                                className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-medium ${getAvatarColor(agent.name)}`}
                              >
                                {getInitials(agent.name)}
                              </div>
                            )}
                            <span className="font-medium text-gray-900">{agent.name}</span>
                          </div>
                        </td>
                        <td className="text-center px-3 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <span className="font-semibold text-gray-900">{agent.average}</span>
                            <StarDisplay rating={Math.round(agent.average)} size="sm" />
                          </div>
                        </td>
                        <td className="text-center px-3 py-3 text-gray-600">{agent.total}</td>
                        <td className="px-3 py-3">
                          <div className="space-y-0.5">
                            {[...agent.distribution].reverse().map((count, i) => {
                              const star = 5 - i
                              const pct = agent.total > 0 ? (count / agent.total) * 100 : 0
                              return (
                                <div key={star} className="flex items-center gap-1.5 text-xs">
                                  <span className="w-3 text-right text-gray-400">{star}</span>
                                  <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-amber-400 rounded-full"
                                      style={{ width: `${pct}%` }}
                                    />
                                  </div>
                                  <span className="w-6 text-right text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity">{count}</span>
                                </div>
                              )
                            })}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Recent comments */}
            {recentComments.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-200">
                  <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Comentários recentes
                  </h2>
                </div>
                <div className="divide-y divide-gray-100">
                  {recentComments.map(comment => (
                    <div key={comment.id} className="px-5 py-4">
                      <div className="flex items-center gap-2 mb-2">
                        {comment.agent_avatar_url ? (
                          <img
                            src={comment.agent_avatar_url}
                            alt={comment.agent_name}
                            className="w-5 h-5 rounded-full object-cover"
                          />
                        ) : (
                          <div
                            className={`w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-medium ${getAvatarColor(comment.agent_name)}`}
                          >
                            {getInitials(comment.agent_name)}
                          </div>
                        )}
                        <span className="text-sm font-medium text-gray-900">{comment.agent_name}</span>
                        <StarDisplay rating={comment.rating} size="sm" />
                        <span className="text-xs text-gray-400 ml-auto">{formatDate(comment.created_at)}</span>
                      </div>
                      <p className="text-sm text-gray-600 pl-7">{comment.comment}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty state */}
            {total === 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <Star className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <h3 className="text-sm font-medium text-gray-900 mb-1">Nenhuma avaliação ainda</h3>
                <p className="text-sm text-gray-500">
                  As avaliações aparecerão aqui quando os clientes avaliarem o atendimento.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
